import { useState } from 'react';
import { connect } from 'react-redux';

import { isValid, parseISO } from 'date-fns';
import PropTypes from 'prop-types';

import { getReservationMultiStatusOptions } from 'shared/constants';
import format from 'shared/lib/date-fns/format';
import { useTransl } from 'shared/lib/i18n';

import ModalComponent from './ModalComponent';

import {
  getAmenityReservations,
  onGetReservationsCalendar,
  setAmenityStatus,
} from './actions';

import {
  checkHasPermissions,
  formatRole,
  getTodayCount,
  isAdmin,
} from '../../utils';
import { getLinkToUser } from '../Messages/utils';
import { getFullName, getRoleName } from '../Person/utils';
import { getContactInfo } from './utils';

import permissions from '../../permissions';

const LIST_KEY = 'amenityReservations';

const ModalContainer = ({
  handleClose,
  reservation,
  onEditReservation,
  listParams,
  getItems,
  onSetAmenityStatus,
  isFrontDesk,
}) => {
  const { t } = useTransl();
  const [chosenStatus, setChosenStatus] = useState('');

  const {
    amenity,
    contactPerson,
    creator,
    comments,
    id,
    status,
    date,
    startTime,
    endTime,
    createdAt,
  } = reservation;

  const createdAtParsed = createdAt && parseISO(createdAt);
  const createdAtFormatted = isValid(createdAtParsed)
    ? format(
        createdAtParsed,
        `'${t('metaInfo.created')}' MM/dd/yy '${t('metaInfo.at')}' h:mmaaa`,
      )
    : null;
  const startsAt = new Date(`${date.substr(0, 10)}T${startTime}`);
  const endsAt = new Date(`${date.substr(0, 10)}T${endTime}`);
  const formattedDate = `${format(startsAt, `EEEE, MM/dd, h:mmaaa`)} — ${format(
    endsAt,
    `h:mmaaa`,
  )}`;

  const statusOptions = getReservationMultiStatusOptions();

  const options =
    checkHasPermissions(permissions.approve_amenity_reservation) && !isAdmin()
      ? statusOptions
      : statusOptions.filter((item) => item.value === 'canceled');

  const handleSelectStatus = (value) => setChosenStatus(value);
  const handleEditReservation = () => onEditReservation(id);
  const formatRoleIdToName = (id) => formatRole(getRoleName(id));

  const personsPhoneNumber = getContactInfo('phone_number', contactPerson);

  const personsEmail = getContactInfo('email', contactPerson);

  const creatorRole = formatRoleIdToName(creator?.role);
  const userRole = formatRoleIdToName(contactPerson?.role);

  const userName = `${getFullName(contactPerson)} ${
    userRole && '-'
  } ${userRole}`;
  const creatorName = `${getFullName(creator)} ${
    creatorRole && '-'
  } ${creatorRole}`;

  const buildingName = `${amenity.building.name}`;
  const buildingId = amenity.building.id;

  const urlToCreator = getLinkToUser(creator, true);
  const urlToContactPerson = getLinkToUser(contactPerson, true);

  const onSaveStatus = async () => {
    const response = await onSetAmenityStatus(id, chosenStatus);
    if (response) {
      !isFrontDesk && getItems(listParams);
      handleClose();
    }
  };

  return (
    <ModalComponent
      date={formattedDate}
      options={options}
      comments={comments}
      userName={userName}
      amenityData={amenity}
      contactPerson={contactPerson}
      personsEmail={personsEmail}
      personsPhoneNumber={personsPhoneNumber}
      handleClose={handleClose}
      handleSelectStatus={handleSelectStatus}
      handleEditReservation={handleEditReservation}
      onSaveStatus={onSaveStatus}
      chosenStatus={chosenStatus}
      initialStatus={status?.status}
      buildingName={buildingName}
      createdAt={createdAtFormatted}
      creatorName={creatorName}
      unitName={contactPerson?.unitName}
      buildingId={buildingId}
      unitId={contactPerson?.unit}
      urlToContactPerson={urlToContactPerson}
      urlToCreator={urlToCreator}
    />
  );
};

const mapStateToProps = ({ lists }) => {
  const listParams = lists?.[LIST_KEY] ?? {};
  return {
    listParams,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const isCalendarView = ownProps?.location?.pathname?.includes('/calendar');
  return {
    getItems: (queryParams) => {
      if (isCalendarView) {
        const params = { ...queryParams, approval_statuses: 'approved' };
        return onGetReservationsCalendar(dispatch, params);
      }
      getAmenityReservations(dispatch, queryParams, false, isCalendarView);
      return getTodayCount(getAmenityReservations, dispatch, queryParams);
    },
    onSetAmenityStatus: (id, status) => setAmenityStatus(dispatch, id, status),
  };
};

ModalContainer.propTypes = {
  handleClose: PropTypes.func.isRequired,
  reservation: PropTypes.instanceOf(Object).isRequired,
  onEditReservation: PropTypes.func.isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  getItems: PropTypes.func.isRequired,
  onSetAmenityStatus: PropTypes.func.isRequired,
  isFrontDesk: PropTypes.bool,
};

export default connect(mapStateToProps, mapDispatchToProps)(ModalContainer);
